/**
 * LocationSelector — Structured address input for mall registration.
 *
 * Props:
 *   value    {object}   — { street_address, city, state, zip_code }
 *   onChange {function} — called with the updated location object
 *   required {boolean}  — when true, all four fields are required (default: true)
 *   disabled {boolean}  — when true, all fields are read-only
 *
 * Validation (Property 6):
 *   All four fields (street_address, city, state, zip_code) must be non-empty
 *   before the parent form can proceed. Errors are shown inline.
 */

import React, { useState, useCallback, useMemo } from 'react';

// US state abbreviations for the dropdown
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC',
];

const FIELD_LABELS = {
  street_address: 'Street Address',
  city:           'City',
  state:          'State',
  zip_code:       'ZIP Code',
};

const FIELD_PLACEHOLDERS = {
  street_address: '9622 Ardrey Woods Dr',
  city:           'Charlotte',
  state:          'NC',
  zip_code:       '28277',
};

const styles = {
  container: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    background: '#fafafa',
    marginBottom: '16px',
  },
  legend: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#4f46e5',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  requiredStar: {
    color: '#e53e3e',
    marginLeft: '2px',
  },
  input: {
    padding: '8px 10px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.15s',
    background: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: '#e53e3e',
    background: '#fff5f5',
  },
  inputFocus: {
    borderColor: '#4f46e5',
  },
  select: {
    padding: '8px 10px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    background: '#fff',
    width: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  errorText: {
    fontSize: '11px',
    color: '#e53e3e',
    marginTop: '2px',
  },
  hint: {
    fontSize: '11px',
    color: '#888',
    marginTop: '8px',
  },
};

/**
 * Validate a location object.
 * Returns an object mapping field names to error strings (empty = valid).
 */
export function validateLocation(location = {}) {
  const errors = {};
  const fields = ['street_address', 'city', 'state', 'zip_code'];
  for (const field of fields) {
    if (!location[field] || !String(location[field]).trim()) {
      errors[field] = `${FIELD_LABELS[field]} is required`;
    }
  }
  // Basic ZIP code format check (5 digits or 5+4)
  if (location.zip_code && !/^\d{5}(-\d{4})?$/.test(location.zip_code.trim())) {
    errors.zip_code = 'ZIP code must be 5 digits (e.g. 28277)';
  }
  return errors;
}

/**
 * Returns true if the location object passes all validation rules.
 */
export function isLocationValid(location = {}) {
  return Object.keys(validateLocation(location)).length === 0;
}

const LocationSelector = ({
  value = {},
  onChange,
  required = true,
  disabled = false,
  showValidation = false,
}) => {
  const [touched, setTouched] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  const location = useMemo(() => ({
    street_address: value.street_address || '',
    city:           value.city           || '',
    state:          value.state          || '',
    zip_code:       value.zip_code       || '',
  }), [value.street_address, value.city, value.state, value.zip_code]);

  const errors = required ? validateLocation(location) : {};

  const handleChange = useCallback((field, val) => {
    const updated = { ...location, [field]: val };
    if (onChange) onChange(updated);
  }, [location, onChange]);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFocusedField(null);
  };

  const shouldShowError = (field) =>
    required && errors[field] && (touched[field] || showValidation);

  const inputStyle = (field) => ({
    ...styles.input,
    ...(shouldShowError(field) ? styles.inputError : {}),
    ...(focusedField === field ? styles.inputFocus : {}),
    ...(disabled ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}),
  });

  return (
    <div style={styles.container} data-testid="location-selector">
      <div style={styles.legend}>
        📍 Mall Physical Location
        {required && <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>
          — all fields required
        </span>}
      </div>

      <div style={styles.grid}>
        {/* Street Address — full width */}
        <div style={{ ...styles.fieldGroup, ...styles.fullWidth }}>
          <label style={styles.label} htmlFor="loc-street">
            {FIELD_LABELS.street_address}
            {required && <span style={styles.requiredStar}>*</span>}
          </label>
          <input
            id="loc-street"
            data-testid="loc-street_address"
            type="text"
            value={location.street_address}
            placeholder={FIELD_PLACEHOLDERS.street_address}
            disabled={disabled}
            style={inputStyle('street_address')}
            onChange={e => handleChange('street_address', e.target.value)}
            onFocus={() => setFocusedField('street_address')}
            onBlur={() => handleBlur('street_address')}
            aria-required={required}
            aria-invalid={shouldShowError('street_address')}
            aria-describedby={shouldShowError('street_address') ? 'loc-street-error' : undefined}
          />
          {shouldShowError('street_address') && (
            <span id="loc-street-error" style={styles.errorText} role="alert">
              {errors.street_address}
            </span>
          )}
        </div>

        {/* City */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="loc-city">
            {FIELD_LABELS.city}
            {required && <span style={styles.requiredStar}>*</span>}
          </label>
          <input
            id="loc-city"
            data-testid="loc-city"
            type="text"
            value={location.city}
            placeholder={FIELD_PLACEHOLDERS.city}
            disabled={disabled}
            style={inputStyle('city')}
            onChange={e => handleChange('city', e.target.value)}
            onFocus={() => setFocusedField('city')}
            onBlur={() => handleBlur('city')}
            aria-required={required}
            aria-invalid={shouldShowError('city')}
            aria-describedby={shouldShowError('city') ? 'loc-city-error' : undefined}
          />
          {shouldShowError('city') && (
            <span id="loc-city-error" style={styles.errorText} role="alert">
              {errors.city}
            </span>
          )}
        </div>

        {/* State */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="loc-state">
            {FIELD_LABELS.state}
            {required && <span style={styles.requiredStar}>*</span>}
          </label>
          <select
            id="loc-state"
            data-testid="loc-state"
            value={location.state}
            disabled={disabled}
            style={{
              ...styles.select,
              ...(shouldShowError('state') ? styles.inputError : {}),
              ...(disabled ? { background: '#f5f5f5', cursor: 'not-allowed' } : {}),
            }}
            onChange={e => handleChange('state', e.target.value)}
            onBlur={() => handleBlur('state')}
            aria-required={required}
            aria-invalid={shouldShowError('state')}
          >
            <option value="">Select state…</option>
            {US_STATES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {shouldShowError('state') && (
            <span style={styles.errorText} role="alert">{errors.state}</span>
          )}
        </div>

        {/* ZIP Code */}
        <div style={styles.fieldGroup}>
          <label style={styles.label} htmlFor="loc-zip">
            {FIELD_LABELS.zip_code}
            {required && <span style={styles.requiredStar}>*</span>}
          </label>
          <input
            id="loc-zip"
            data-testid="loc-zip_code"
            type="text"
            value={location.zip_code}
            placeholder={FIELD_PLACEHOLDERS.zip_code}
            disabled={disabled}
            maxLength={10}
            style={inputStyle('zip_code')}
            onChange={e => handleChange('zip_code', e.target.value)}
            onFocus={() => setFocusedField('zip_code')}
            onBlur={() => handleBlur('zip_code')}
            aria-required={required}
            aria-invalid={shouldShowError('zip_code')}
            aria-describedby={shouldShowError('zip_code') ? 'loc-zip-error' : undefined}
          />
          {shouldShowError('zip_code') && (
            <span id="loc-zip-error" style={styles.errorText} role="alert">
              {errors.zip_code}
            </span>
          )}
        </div>
      </div>

      {!required && (
        <p style={styles.hint}>
          Location is optional but recommended for mall tenants.
        </p>
      )}
    </div>
  );
};

export default LocationSelector;
